<template>
  <div class="aml-detail-root">

    <!-- ══ Loading skeleton ══ -->
    <div v-if="loading" class="aml-detail-wrap">
      <div class="aml-detail-header">
        <v-btn icon disabled class="mr-2"><v-icon>mdi-arrow-left</v-icon></v-btn>
        <div class="flex-grow-1">
          <v-skeleton-loader type="heading" width="220" class="mb-1" />
          <v-skeleton-loader type="text" width="300" />
        </div>
        <v-skeleton-loader type="chip" width="90" class="mr-2" />
        <v-skeleton-loader type="chip" width="70" />
      </div>
      <v-skeleton-loader type="tabs" class="mb-4" />
      <v-row>
        <v-col cols="12" md="7">
          <v-skeleton-loader type="card" height="220" class="mb-4" />
          <v-skeleton-loader type="card" height="160" />
        </v-col>
        <v-col cols="12" md="5">
          <v-skeleton-loader type="card" height="260" />
        </v-col>
      </v-row>
    </div>

    <!-- ══ Error state ══ -->
    <div v-else-if="!amlCase" class="aml-detail-wrap text-center py-12">
      <v-icon size="52" color="grey lighten-1">mdi-file-alert-outline</v-icon>
      <div class="subtitle-1 grey--text mt-3">Case could not be loaded</div>
      <div class="caption grey--text mb-4">The case may not exist or you may not have access.</div>
      <v-btn depressed small color="primary" @click="$router.push({ name: 'aml-case-dashboard' })">
        <v-icon small left>mdi-arrow-left</v-icon>Back to dashboard
      </v-btn>
      <v-btn depressed small class="ml-2" @click="loadCase">
        <v-icon small left>mdi-refresh</v-icon>Retry
      </v-btn>
    </div>

    <!-- ══ Loaded content ══ -->
    <div v-else class="aml-detail-wrap">

      <!-- ── Page header ── -->
      <div class="aml-detail-header">
        <button class="aml-back-btn" @click="$router.push({ name: 'aml-case-dashboard' })">
          <v-icon small>mdi-arrow-left</v-icon>
        </button>

        <div class="flex-grow-1 ml-3">
          <div class="aml-case-ref">{{ amlCase.caseReference }}</div>
          <div class="aml-case-sub">
            Opened by {{ assigneeName(amlCase.startedBy) }} on {{ formatDate(amlCase.dateCreated, 'YYYY-MM-DD') }}
          </div>
        </div>

        <!-- Status / priority pills -->
        <div class="d-flex align-center" style="gap:6px;flex-wrap:wrap">
          <span v-if="!isClosed" class="aml-status-pill aml-pill-blue">
            <v-icon x-small left>mdi-progress-clock</v-icon>
            {{ amlCase.reportStage || 'Open' }}
          </span>
          <span v-else class="aml-status-pill" :style="closedPillStyle(amlCase.status)">
            <v-icon x-small left>mdi-lock-outline</v-icon>
            {{ formatStatus(amlCase.status) }}
          </span>
          <span class="aml-status-pill" :style="priorityPillStyle(amlCase.priority)">
            {{ amlCase.priority }}
          </span>

          <!-- Open-case actions — only visible to the assigned officer, and only while the case is open -->
          <template v-if="canModifyStages">
            <div class="aml-header-divider" />
            <button class="aml-hdr-btn" @click="actionDialog = true">
              <v-icon x-small left>mdi-plus-circle-outline</v-icon>Add action
            </button>
            <button class="aml-hdr-btn aml-hdr-btn--danger" @click="closeDialog = true">
              <v-icon x-small left>mdi-lock-outline</v-icon>Close case
            </button>
          </template>

          <!-- Read-only indicator — shown when viewing a case not assigned to you -->
          <template v-if="!isClosed && !isAssigned">
            <div class="aml-header-divider" />
            <span class="aml-readonly-badge">
              <v-icon x-small left>mdi-eye-outline</v-icon>Read only
            </span>
          </template>
        </div>
      </div>

      <!-- ── Tabs ── -->
      <div class="aml-tabs-bar">
        <button
          v-for="(t, i) in tabDefs"
          :key="i"
          class="aml-tab"
          :class="{ 'aml-tab--active': tab === i }"
          @click="tab = i"
        >
          {{ t.label }}
          <span v-if="t.badge !== undefined" class="aml-tab-badge" :class="t.badgeDone ? 'aml-tab-badge--done' : ''">
            {{ t.badge }}
          </span>
        </button>
      </div>

      <!-- ══════════════════════════════════════
           TAB 0 — Overview
      ══════════════════════════════════════ -->
      <div v-show="tab === 0" class="aml-tab-content">
        <v-row dense class="mb-3">

          <!-- Account holder -->
          <v-col cols="12" md="6">
            <div class="aml-panel">
              <div class="aml-panel-head">
                <div class="aml-panel-icon" style="background:#E6F1FB">
                  <v-icon small color="#185FA5">mdi-account-outline</v-icon>
                </div>
                <span class="aml-panel-title">Account holder</span>
                <v-spacer />
                <span v-if="accountPerson && accountPerson.memberCategory" class="aml-tag aml-tag-blue">
                  {{ capitalizeName(accountPerson.memberCategory) }}
                </span>
              </div>

              <div v-if="accountPerson" class="pa-3">
                <!-- Name pill -->
                <div class="aml-person-pill mb-3">
                  <div class="aml-person-avatar">{{ personInitials(accountPerson) }}</div>
                  <div class="flex-grow-1">
                    <div class="aml-person-name">{{ capitalizeName((accountPerson.firstName || '') + ' ' + (accountPerson.lastName || '')) }}</div>
                    <div class="aml-person-sub">TRN: {{ accountPerson.trn || '—' }}</div>
                  </div>
                  <div v-if="accountPerson.riskRating" class="text-right">
                    <span class="aml-tag" :style="riskTagStyle(accountPerson.riskRating)">{{ capitalizeName(accountPerson.riskRating) }}</span>
                    <div class="aml-label mt-1">risk rating</div>
                  </div>
                </div>

                <!-- Identity -->
                <div class="aml-section-label">Identity</div>
                <div class="aml-grid-2 mb-2">
                  <div v-if="accountPerson.dateOfBirth" class="aml-field">
                    <div class="aml-label">Date of birth</div>
                    <div class="aml-value">{{ formatDate(accountPerson.dateOfBirth, 'YYYY-MM-DD') }}</div>
                  </div>
                  <div v-if="accountPerson.gender" class="aml-field">
                    <div class="aml-label">Gender</div>
                    <div class="aml-value">{{ capitalizeName(accountPerson.gender) }}</div>
                  </div>
                  <div v-if="accountPerson.maritalStatus" class="aml-field">
                    <div class="aml-label">Marital status</div>
                    <div class="aml-value">{{ capitalizeName(accountPerson.maritalStatus) }}</div>
                  </div>
                  <div v-if="personIds.length" class="aml-field">
                    <div class="aml-label">Primary ID</div>
                    <div class="aml-value">{{ capitalizeName(personIds[0].type) }}: {{ personIds[0].number || personIds[0].value || '—' }}</div>
                  </div>
                </div>

                <!-- Contact -->
                <div class="aml-divider my-2" />
                <div class="aml-section-label">Contact</div>
                <div v-if="accountPerson.emailAddress" class="d-flex align-center mb-1">
                  <v-icon x-small color="#aaa" class="mr-1">mdi-email-outline</v-icon>
                  <span class="aml-value">{{ accountPerson.emailAddress }}</span>
                </div>
                <div v-if="personPhone" class="d-flex align-center mb-1">
                  <v-icon x-small color="#aaa" class="mr-1">mdi-phone-outline</v-icon>
                  <span class="aml-value">{{ personPhone }}</span>
                </div>
                <div v-if="personAddress" class="d-flex align-start mb-1">
                  <v-icon x-small color="#aaa" class="mr-1 mt-1">mdi-map-marker-outline</v-icon>
                  <span class="aml-value">{{ personAddress }}</span>
                </div>

                <!-- Employer -->
                <template v-if="personEmployer">
                  <div class="aml-divider my-2" />
                  <div class="aml-section-label">Employment</div>
                  <div class="aml-grid-2">
                    <div v-if="personEmployer.name || personEmployer.employerName" class="aml-field">
                      <div class="aml-label">Employer</div>
                      <div class="aml-value">{{ capitalizeName(personEmployer.name || personEmployer.employerName) }}</div>
                    </div>
                    <div v-if="personEmployer.occupation || personEmployer.position" class="aml-field">
                      <div class="aml-label">Position</div>
                      <div class="aml-value">{{ capitalizeName(personEmployer.occupation || personEmployer.position) }}</div>
                    </div>
                    <div v-if="personEmployer.income || personEmployer.salary" class="aml-field">
                      <div class="aml-label">Income</div>
                      <div class="aml-value">{{ asMoney(personEmployer.income || personEmployer.salary) }}</div>
                    </div>
                  </div>
                </template>
              </div>

              <div v-else class="text-center py-6 aml-label">
                <v-icon size="32" color="grey lighten-2">mdi-account-off-outline</v-icon>
                <div class="mt-1">No account holder information available</div>
              </div>
            </div>
          </v-col>

          <!-- Account details -->
          <v-col cols="12" md="6">
            <div class="aml-panel">
              <div class="aml-panel-head">
                <div class="aml-panel-icon" style="background:#E1F5EE">
                  <v-icon small color="#0F6E56">mdi-bank-outline</v-icon>
                </div>
                <span class="aml-panel-title">Account details</span>
                <v-spacer />
                <span v-if="accountData.accountStatus" class="aml-tag" :style="accountStatusStyle(accountData.accountStatus)">
                  {{ capitalizeName(accountData.accountStatus) }}
                </span>
              </div>

              <div class="pa-3">
                <!-- Account number pill -->
                <div class="aml-acct-pill mb-3">
                  <v-icon color="#0F6E56" class="mr-3">mdi-card-account-details-outline</v-icon>
                  <div>
                    <div class="aml-person-name">{{ accountData.mainAccountNumber || '—' }}</div>
                    <div class="aml-person-sub">{{ capitalizeName(accountData.accountType) || 'Account number' }}</div>
                  </div>
                </div>

                <div class="aml-section-label">Account info</div>
                <div class="aml-grid-2 mb-2">
                  <div v-if="accountData.branch" class="aml-field">
                    <div class="aml-label">Branch</div>
                    <div class="aml-value">{{ accountData.branch.branchName || '—' }}</div>
                    <div v-if="accountData.branch.branchCode" class="aml-label">{{ accountData.branch.branchCode }}</div>
                  </div>
                  <div v-if="accountData.accountOpeningDate || accountData.dateCreated" class="aml-field">
                    <div class="aml-label">Date opened</div>
                    <div class="aml-value">{{ formatDate(accountData.accountOpeningDate || accountData.dateCreated, 'YYYY-MM-DD') }}</div>
                  </div>
                  <div v-if="accountData.accountLastTransactionDate" class="aml-field">
                    <div class="aml-label">Last transaction</div>
                    <div class="aml-value">{{ formatDate(accountData.accountLastTransactionDate, 'YYYY-MM-DD') }}</div>
                  </div>
                  <div v-if="accountData.verificationStatus" class="aml-field">
                    <div class="aml-label">Verification</div>
                    <div class="aml-value">{{ capitalizeName(accountData.verificationStatus) }}</div>
                  </div>
                  <div v-if="accountData.accountOpeningMethod" class="aml-field">
                    <div class="aml-label">Opening method</div>
                    <div class="aml-value">{{ capitalizeName(accountData.accountOpeningMethod) }}</div>
                  </div>
                  <div v-if="accountData.accountDueDiligenceRating" class="aml-field">
                    <div class="aml-label">Due diligence</div>
                    <span class="aml-tag aml-tag-blue">{{ capitalizeName(accountData.accountDueDiligenceRating) }}</span>
                  </div>
                </div>

                <!-- Sub-accounts -->
                <template v-if="accountData.subAccount && accountData.subAccount.length">
                  <div class="aml-divider my-2" />
                  <div class="aml-section-label">Sub-accounts</div>
                  <div v-for="sub in accountData.subAccount" :key="sub.id" class="d-flex align-center mb-1">
                    <v-icon x-small :color="sub.type === 'SAVINGS' ? 'teal' : sub.type === 'LOAN' ? 'orange' : 'purple'" class="mr-1">
                      {{ sub.type === 'SAVINGS' ? 'mdi-piggy-bank-outline' : sub.type === 'LOAN' ? 'mdi-credit-card-outline' : 'mdi-shield-outline' }}
                    </v-icon>
                    <span class="aml-value mr-2">{{ capitalizeName(sub.type) }}</span>
                    <span class="aml-tag" :style="sub.status === 'ACTIVE' ? 'background:#EAF3DE;color:#3B6D11' : 'background:#F1EFE8;color:#5F5E5A'">{{ capitalizeName(sub.status) }}</span>
                  </div>
                </template>

                <!-- KYC -->
                <template v-if="personKyc">
                  <div class="aml-divider my-2" />
                  <div class="aml-section-label">KYC risk score</div>
                  <div class="d-flex align-center mt-1">
                    <div class="aml-kyc-track mr-3">
                      <div class="aml-kyc-bar" :style="`width:${personKyc.score}%;background:${kycColor(personKyc.level)}`" />
                    </div>
                    <span class="aml-value font-weight-medium mr-1">{{ personKyc.score }}</span>
                    <span class="aml-label">· {{ capitalizeName(personKyc.level) }} risk</span>
                  </div>
                  <div v-if="personKyc.lastUpdated" class="aml-label mt-1">Last assessed {{ timeAgo(personKyc.lastUpdated) }}</div>
                </template>

                <!-- Watchlist hits -->
                <template v-if="personWatchlistMatches.length">
                  <div class="aml-divider my-2" />
                  <div class="d-flex align-center mb-1">
                    <v-icon small color="#E24B4A" class="mr-1">mdi-alert-circle-outline</v-icon>
                    <span class="aml-value" style="color:#A32D2D;font-weight:600">{{ personWatchlistMatches.length }} watchlist hit{{ personWatchlistMatches.length > 1 ? 's' : '' }}</span>
                  </div>
                  <div v-for="(match, i) in personWatchlistMatches.slice(0, 2)" :key="i" class="aml-label">
                    {{ capitalizeName(match.listName || match.list || match.provider || 'Unknown list') }}
                  </div>
                </template>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- AML investigation context -->
        <v-row dense class="mb-3" v-if="personSourceOfFunds.length || personSourceOfWealth.length || personIds.length > 1">
          <v-col cols="12">
            <div class="aml-panel">
              <div class="aml-panel-head">
                <div class="aml-panel-icon" style="background:#FAEEDA">
                  <v-icon small color="#854F0B">mdi-magnify-scan</v-icon>
                </div>
                <span class="aml-panel-title">AML investigation context</span>
              </div>
              <div class="pa-3">
                <v-row dense>
                  <v-col cols="12" md="4" v-if="personSourceOfFunds.length">
                    <div class="aml-section-label mb-2"><v-icon x-small class="mr-1">mdi-cash-check</v-icon>Source of funds</div>
                    <span v-for="(sof, i) in personSourceOfFunds.slice(0, 4)" :key="'sof-' + i" class="aml-tag aml-tag-blue mr-1 mb-1">
                      {{ capitalizeName(sof.source || sof.type || sof.name || String(sof)) }}
                    </span>
                  </v-col>
                  <v-col cols="12" md="4" v-if="personSourceOfWealth.length">
                    <div class="aml-section-label mb-2"><v-icon x-small class="mr-1">mdi-briefcase-outline</v-icon>Source of wealth</div>
                    <span v-for="(sow, i) in personSourceOfWealth.slice(0, 4)" :key="'sow-' + i" class="aml-tag aml-tag-purple mr-1 mb-1">
                      {{ capitalizeName(sow.source || sow.type || sow.name || String(sow)) }}
                    </span>
                  </v-col>
                  <v-col cols="12" md="4" v-if="personIds.length">
                    <div class="aml-section-label mb-2"><v-icon x-small class="mr-1">mdi-card-account-details-outline</v-icon>Identification</div>
                    <div v-for="(doc, i) in personIds" :key="'id-' + i" class="mb-1">
                      <span class="aml-value font-weight-medium">{{ capitalizeName(doc.type) }}</span>
                      <span class="aml-label ml-1">{{ doc.number || doc.value || '—' }}</span>
                      <span v-if="doc.expiryDate || doc.expiry" class="aml-label ml-1">· Exp: {{ formatDate(doc.expiryDate || doc.expiry, 'YYYY-MM-DD') }}</span>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Case details + trends -->
        <v-row dense>
          <!-- Case details -->
          <v-col cols="12" md="7">
            <div class="aml-panel">
              <div class="aml-panel-head">
                <div class="aml-panel-icon" style="background:#E6F1FB">
                  <v-icon small color="#185FA5">mdi-folder-outline</v-icon>
                </div>
                <span class="aml-panel-title">Case details</span>
                <v-spacer />
                <span class="aml-tag" :style="priorityPillStyle(amlCase.priority)">{{ amlCase.priority }} priority</span>
              </div>
              <div class="pa-3">
                <div class="aml-grid-2">
                  <div class="aml-field">
                    <div class="aml-label">Report type</div>
                    <div class="aml-value">{{ amlCase.reportType }}</div>
                  </div>
                  <div class="aml-field">
                    <div class="aml-label">Current stage</div>
                    <div class="aml-value font-weight-medium">{{ amlCase.reportStage || (isClosed ? formatStatus(amlCase.status) : 'Not started') }}</div>
                  </div>
                  <div class="aml-field">
                    <div class="aml-label">Assigned to</div>
                    <div class="aml-value">{{ assigneeName(amlCase.assignedTo) }}</div>
                  </div>
                  <div class="aml-field">
                    <div class="aml-label">Opened by</div>
                    <div class="aml-value">{{ assigneeName(amlCase.startedBy) }}</div>
                  </div>
                  <div class="aml-field">
                    <div class="aml-label">Date opened</div>
                    <div class="aml-value">{{ formatDate(amlCase.dateCreated, 'YYYY-MM-DD') }}</div>
                  </div>
                  <div v-if="amlCase.regulatoryReportId" class="aml-field">
                    <div class="aml-label">Regulatory report</div>
                    <div class="aml-value">{{ amlCase.regulatoryReportId }}</div>
                  </div>
                </div>
                <div class="aml-field mt-2">
                  <div class="aml-label">Summary</div>
                  <div class="aml-value">{{ amlCase.summary || '—' }}</div>
                </div>

                <!-- Closure info -->
                <template v-if="amlCase.dateClosed">
                  <div class="aml-divider my-3" />
                  <div class="d-flex align-center mb-2">
                    <v-icon x-small color="#888" class="mr-1">mdi-lock-outline</v-icon>
                    <span class="aml-section-label">Case closure</span>
                  </div>
                  <div class="aml-grid-2">
                    <div class="aml-field">
                      <div class="aml-label">Outcome</div>
                      <div class="aml-value font-weight-medium">{{ formatStatus(amlCase.status) }}</div>
                    </div>
                    <div class="aml-field">
                      <div class="aml-label">Date closed</div>
                      <div class="aml-value">{{ formatDate(amlCase.dateClosed, 'YYYY-MM-DD') }}</div>
                    </div>
                    <div class="aml-field" style="grid-column:span 2">
                      <div class="aml-label">Reason</div>
                      <div class="aml-value">{{ amlCase.closeReason }}</div>
                    </div>
                    <div v-if="amlCase.closeComment" class="aml-field" style="grid-column:span 2">
                      <div class="aml-label">Comment</div>
                      <div class="aml-value aml-label">{{ amlCase.closeComment }}</div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </v-col>

          <!-- Account trends -->
          <v-col cols="12" md="5">
            <div class="aml-panel">
              <div class="aml-panel-head">
                <div class="aml-panel-icon" style="background:#EEEDFE">
                  <v-icon small color="#534AB7">mdi-chart-line</v-icon>
                </div>
                <span class="aml-panel-title">Account trends</span>
                <v-spacer />
                <span class="aml-tag" style="background:#F1EFE8;color:#5F5E5A">Last 12 months</span>
              </div>
              <div class="pa-3">
                <div v-if="!accountTrends.length" class="text-center py-6 aml-label">
                  <v-icon size="36" color="grey lighten-2">mdi-chart-line</v-icon>
                  <div class="mt-2">No trend data available.</div>
                </div>
                <div v-else class="aml-trend-grid">
                  <div v-for="(trend, i) in accountTrends" :key="i" class="aml-trend-card">
                    <div class="d-flex align-center mb-1">
                      <v-icon small :color="trendChangeColor(trend.change)" class="mr-1">{{ trendIcon(trend.name) }}</v-icon>
                      <span class="aml-label">{{ formatTrendName(trend.name) }}</span>
                    </div>
                    <div class="aml-trend-value">{{ formatTrendValue(trend) }}</div>
                    <div class="aml-label mt-1">
                      <span :style="trendChangeColor(trend.change) === 'success' ? 'color:#3B6D11;font-weight:600' : 'color:#A32D2D;font-weight:600'">
                        {{ formatTrendChange(trend.change) }}
                      </span>
                      <span class="ml-1">vs prior</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- ══════════════════════════════════════
           TAB 1 — Transactions
      ══════════════════════════════════════ -->
      <div v-show="tab === 1" class="aml-tab-content">
        <div class="aml-panel">
          <div class="aml-panel-head">
            <span class="aml-panel-title">Linked transactions</span>
            <v-spacer />
            <!-- Link buttons only available to the assigned officer, and only while the case is open -->
            <template v-if="canModifyTransactions">
            <button class="aml-hdr-btn mr-2" @click="openAttemptPickerDialog">
              <v-icon x-small left>mdi-flag-plus</v-icon>Link attempt
            </button>
            <!-- <button class="aml-hdr-btn" @click="txnDialog = true">
              <v-icon x-small left>mdi-identifier</v-icon>Link by ID
            </button> -->
            </template>
          </div>
          <div class="aml-table-wrap">
            <table class="aml-table">
              <colgroup>
                <col style="width:9%">
                <col style="width:13%">
                <col style="width:9%">
                <col style="width:10%">
                <col style="width:10%">
                <col style="width:10%">
                <col style="width:11%">
                <col style="width:14%">
                <col style="width:9%">
                <col style="width:5%">
              </colgroup>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Transaction ID</th>
                  <th>Txn date</th>
                  <th>Amount</th>
                  <th>Direction</th>
                  <th>Method</th>
                  <th>Location</th>
                  <th>Description</th>
                  <th>Reason linked</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!amlCase.caseTransactions || !amlCase.caseTransactions.length">
                  <td colspan="10" class="aml-empty-row">No transactions linked yet</td>
                </tr>
                <tr
                  v-for="row in linkedTxnRows"
                  :key="row.link.id"
                  class="aml-table-row aml-txn-row--clickable"
                  @click="openTxnDrawer(row.link)"
                >
                  <td>
                    <span class="aml-tag" :style="row.v.kind === 'transaction' ? 'background:#E6F1FB;color:#185FA5' : 'background:#FAEEDA;color:#854F0B'">
                      {{ row.v.kind === 'transaction' ? 'Transaction' : 'Attempt' }}
                    </span>
                  </td>
                  <td style="font-family:monospace;font-size:10px;color:#555">
                    {{ row.v.code || '—' }}
                  </td>
                  <td class="aml-label">
                    {{ formatDate(row.v.date || row.link.transactionDate, 'YYYY-MM-DD') }}
                  </td>
                  <td class="aml-value" style="font-weight:600">
                    <template v-if="row.v.amount != null">
                      <span :style="row.v.type === 'CREDIT' ? 'color:#1D9E75' : (row.v.type === 'DEBIT' ? 'color:#E24B4A' : 'color:#333')">
                        {{ row.v.type === 'CREDIT' ? '+' : (row.v.type === 'DEBIT' ? '-' : '') }}{{ row.v.currency ? row.v.currency + ' ' : '' }}{{ asMoney(row.v.amount) }}
                      </span>
                    </template>
                    <span v-else class="aml-label">—</span>
                  </td>
                  <td>
                    <span v-if="row.v.type === 'CREDIT'" class="aml-tag" style="background:#E1F5EE;color:#0F6E56">Credit</span>
                    <span v-else-if="row.v.type === 'DEBIT'" class="aml-tag" style="background:#FCEBEB;color:#A32D2D">Debit</span>
                    <span v-else-if="row.v.type" class="aml-tag" style="background:#EEF0F3;color:#4B5563">{{ row.v.type }}</span>
                    <span v-else class="aml-label">—</span>
                  </td>
                  <td class="aml-label">
                    {{ row.v.method || '—' }}
                  </td>
                  <td class="aml-label aml-truncate-cell">
                    {{ row.v.location || '—' }}
                  </td>
                  <td class="aml-label aml-truncate-cell">
                    {{ row.v.description || '—' }}
                  </td>
                  <td class="aml-label aml-truncate-cell">{{ row.link.reason || '—' }}</td>
                  <td @click.stop>
                    <div class="d-flex align-center" style="gap:4px">
                      <button class="aml-icon-btn" @click="openTxnDrawer(row.link)" title="View details">
                        <v-icon x-small color="#185FA5">mdi-eye</v-icon>
                      </button>
                      <!-- Unlink only available to the assigned officer, and only while the case is open -->
                      <button v-if="canModifyTransactions" class="aml-icon-btn" @click="confirmRemoveTxn(row.link)" title="Unlink">
                        <v-icon x-small color="#E24B4A">mdi-link-off</v-icon>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ══ Transaction detail drawer ══ -->
      <v-navigation-drawer
        v-model="txnDrawer"
        app
        right
        temporary
        width="420"
        style="z-index:300"
      >
        <div v-if="txnDrawerItem && txnDrawerView" class="txn-drawer">

          <!-- Drawer header -->
          <div class="txn-drawer-header">
            <div>
              <div class="txn-drawer-title">{{ txnDrawerView.kind === 'attempt' ? 'Flagged Attempt Detail' : 'Transaction Detail' }}</div>
              <div class="txn-drawer-sub" style="font-family:monospace;font-size:11px">
                {{ txnDrawerView.code }}
              </div>
            </div>
            <button class="aml-icon-btn" @click="txnDrawer = false">
              <v-icon small>mdi-close</v-icon>
            </button>
          </div>

          <!-- Amount hero -->
          <div class="txn-drawer-amount-block" :class="txnDrawerView.type === 'CREDIT' ? 'txn-credit-bg' : 'txn-debit-bg'">
            <div class="txn-drawer-amount-label">
              <template v-if="txnDrawerView.kind === 'attempt'">
                Flagged attempt{{ txnDrawerView.type ? ' — ' + txnDrawerView.type : '' }}
              </template>
              <template v-else>
                {{ txnDrawerView.type === 'CREDIT' ? 'Credit (Deposit)' : 'Debit (Withdrawal)' }}
              </template>
            </div>
            <div class="txn-drawer-amount-value" :style="txnDrawerView.type === 'CREDIT' ? 'color:#0F6E56' : 'color:#A32D2D'">
              {{ txnDrawerView.type === 'CREDIT' ? '+' : (txnDrawerView.type === 'DEBIT' ? '-' : '') }}{{ txnDrawerView.currency ? txnDrawerView.currency + ' ' : '' }}{{ txnDrawerView.amount != null ? asMoney(txnDrawerView.amount) : '—' }}
            </div>
            <div v-if="txnDrawerView.balanceAfter != null" class="txn-drawer-balance">
              Balance after: {{ asMoney(txnDrawerView.balanceAfter) }}
            </div>
          </div>

          <!-- Detail fields -->
          <div class="txn-drawer-body">

            <div class="txn-drawer-section-label">Transaction info</div>
            <div class="txn-drawer-row">
              <span class="txn-drawer-field-label">Date</span>
              <span class="txn-drawer-field-value">{{ formatDate(txnDrawerView.date, 'MMMM D, YYYY') }}</span>
            </div>
            <div class="txn-drawer-row">
              <span class="txn-drawer-field-label">Method</span>
              <span class="txn-drawer-field-value">{{ txnDrawerView.method || '—' }}</span>
            </div>
            <div class="txn-drawer-row">
              <span class="txn-drawer-field-label">{{ txnDrawerView.kind === 'attempt' ? 'Reference' : 'Code' }}</span>
              <span class="txn-drawer-field-value" style="font-family:monospace">{{ txnDrawerView.code || '—' }}</span>
            </div>
            <div v-if="txnDrawerView.location" class="txn-drawer-row">
              <span class="txn-drawer-field-label">Location</span>
              <span class="txn-drawer-field-value">{{ txnDrawerView.location }}</span>
            </div>
            <div v-if="txnDrawerView.description" class="txn-drawer-row">
              <span class="txn-drawer-field-label">Description</span>
              <span class="txn-drawer-field-value">{{ txnDrawerView.description }}</span>
            </div>
            <div v-if="txnDrawerView.chequeNumber" class="txn-drawer-row">
              <span class="txn-drawer-field-label">Cheque #</span>
              <span class="txn-drawer-field-value" style="font-family:monospace">{{ txnDrawerView.chequeNumber }}</span>
            </div>
            <div v-if="txnDrawerView.sourceOfFunds" class="txn-drawer-row">
              <span class="txn-drawer-field-label">Source of funds</span>
              <span class="txn-drawer-field-value">{{ txnDrawerView.sourceOfFunds }}</span>
            </div>

            <!-- Attempt-only: suspicion details -->
            <template v-if="txnDrawerView.kind === 'attempt'">
              <div class="txn-drawer-divider" />

              <div class="txn-drawer-section-label">Suspicion</div>
              <div class="txn-drawer-row">
                <span class="txn-drawer-field-label">Reason</span>
                <span class="txn-drawer-field-value">{{ txnDrawerView.suspicionReason || '—' }}</span>
              </div>
              <div v-if="txnDrawerView.suspicionCategory" class="txn-drawer-row">
                <span class="txn-drawer-field-label">Category</span>
                <span class="txn-drawer-field-value">{{ txnDrawerView.suspicionCategory }}</span>
              </div>
              <div v-if="txnDrawerView.flaggedBy" class="txn-drawer-row">
                <span class="txn-drawer-field-label">Flagged by</span>
                <span class="txn-drawer-field-value">{{ assigneeName(txnDrawerView.flaggedBy) }}</span>
              </div>

              <div class="txn-drawer-divider" />

              <div class="txn-drawer-section-label">Customer</div>
              <div class="txn-drawer-row">
                <span class="txn-drawer-field-label">Name</span>
                <span class="txn-drawer-field-value">{{ txnDrawerView.customerName || '—' }}</span>
              </div>
              <div v-if="txnDrawerView.idNumber" class="txn-drawer-row">
                <span class="txn-drawer-field-label">ID</span>
                <span class="txn-drawer-field-value">{{ txnDrawerView.idNumber }}{{ txnDrawerView.idType ? ' (' + txnDrawerView.idType + ')' : '' }}</span>
              </div>
              <div v-if="txnDrawerView.trn" class="txn-drawer-row">
                <span class="txn-drawer-field-label">TRN</span>
                <span class="txn-drawer-field-value" style="font-family:monospace">{{ txnDrawerView.trn }}</span>
              </div>
              <div v-if="txnDrawerView.accountNumber" class="txn-drawer-row">
                <span class="txn-drawer-field-label">Account #</span>
                <span class="txn-drawer-field-value" style="font-family:monospace">{{ txnDrawerView.accountNumber }}</span>
              </div>
              <div v-if="txnDrawerView.cellNumber" class="txn-drawer-row">
                <span class="txn-drawer-field-label">Cell</span>
                <span class="txn-drawer-field-value">{{ txnDrawerView.cellNumber }}</span>
              </div>

              <template v-if="txnDrawerView.senderName || txnDrawerView.senderAccountNumber">
                <div class="txn-drawer-divider" />
                <div class="txn-drawer-section-label">Sender</div>
                <div v-if="txnDrawerView.senderName" class="txn-drawer-row">
                  <span class="txn-drawer-field-label">Name</span>
                  <span class="txn-drawer-field-value">{{ txnDrawerView.senderName }}</span>
                </div>
                <div v-if="txnDrawerView.senderAccountNumber" class="txn-drawer-row">
                  <span class="txn-drawer-field-label">Account #</span>
                  <span class="txn-drawer-field-value" style="font-family:monospace">{{ txnDrawerView.senderAccountNumber }}</span>
                </div>
              </template>

              <template v-if="txnDrawerView.receiverName || txnDrawerView.receiverAccountNumber">
                <div class="txn-drawer-divider" />
                <div class="txn-drawer-section-label">Receiver</div>
                <div v-if="txnDrawerView.receiverName" class="txn-drawer-row">
                  <span class="txn-drawer-field-label">Name</span>
                  <span class="txn-drawer-field-value">{{ txnDrawerView.receiverName }}</span>
                </div>
                <div v-if="txnDrawerView.receiverAccountNumber" class="txn-drawer-row">
                  <span class="txn-drawer-field-label">Account #</span>
                  <span class="txn-drawer-field-value" style="font-family:monospace">{{ txnDrawerView.receiverAccountNumber }}</span>
                </div>
              </template>
            </template>

            <!-- Real-transaction-only: account & sub-account -->
            <template v-else>
              <div class="txn-drawer-divider" />

              <div class="txn-drawer-section-label">Account &amp; sub-account</div>
              <div class="txn-drawer-row">
                <span class="txn-drawer-field-label">Sub-account #</span>
                <span class="txn-drawer-field-value" style="font-family:monospace">{{ txnDrawerView.subAccountNumber || '—' }}</span>
              </div>
              <div v-if="txnDrawerView.tellerId" class="txn-drawer-row">
                <span class="txn-drawer-field-label">Teller ID</span>
                <span class="txn-drawer-field-value" style="font-family:monospace">{{ txnDrawerView.tellerId }}</span>
              </div>
            </template>

            <div class="txn-drawer-divider" />

            <div class="txn-drawer-section-label">Case link</div>
            <div class="txn-drawer-row">
              <span class="txn-drawer-field-label">Linked on</span>
              <span class="txn-drawer-field-value">{{ formatDate(txnDrawerItem.dateAdded || txnDrawerItem.dateCreated, 'YYYY-MM-DD') }}</span>
            </div>
            <div class="txn-drawer-row">
              <span class="txn-drawer-field-label">Added by</span>
              <span class="txn-drawer-field-value">{{ assigneeName(txnDrawerItem.addedBy) }}</span>
            </div>
            <div v-if="txnDrawerItem.reason" class="txn-drawer-row">
              <span class="txn-drawer-field-label">Reason</span>
              <span class="txn-drawer-field-value">{{ txnDrawerItem.reason }}</span>
            </div>
          </div>

          <!-- Drawer footer actions -->
          <div class="txn-drawer-footer">
            <button class="aml-action-btn aml-action-btn--ghost" @click="txnDrawer = false">Close</button>
            <button v-if="canModifyTransactions" class="aml-action-btn" style="background:#FCEBEB;color:#A32D2D;border:0.5px solid #F09595" @click="confirmRemoveTxn(txnDrawerItem); txnDrawer = false">
              <v-icon x-small left>mdi-link-off</v-icon>Unlink
            </button>
          </div>
        </div>
      </v-navigation-drawer>

      <!-- ══════════════════════════════════════
           TAB 2 — Comments
      ══════════════════════════════════════ -->
      <div v-show="tab === 2" class="aml-tab-content">
        <div class="d-flex align-center justify-space-between mb-3">
          <span class="aml-panel-title">{{ amlCase.comments.length }} comment{{ amlCase.comments.length !== 1 ? 's' : '' }}</span>
          <button class="aml-hdr-btn aml-hdr-btn--primary" @click="openCommentDialog()">
            <v-icon x-small left>mdi-comment-plus-outline</v-icon>Add comment
          </button>
        </div>

        <div v-if="!amlCase.comments.length" class="aml-panel text-center py-10 aml-label">
          <v-icon size="40" color="grey lighten-2">mdi-comment-off-outline</v-icon>
          <div class="mt-2">No comments yet. Add one to start the conversation.</div>
        </div>

        <div v-for="comment in amlCase.comments" :key="comment.id" class="aml-comment-card mb-3">
          <div class="aml-comment-header">
            <div class="aml-comment-avatar">{{ initials(comment.user) }}</div>
            <div class="flex-grow-1 ml-2">
              <div class="aml-value" style="font-size:13px">{{ assigneeName(comment.user) }}</div>
              <div class="aml-label">{{ timeAgo(comment.dateCreated) }} · {{ formatDate(comment.dateCreated, 'YYYY-MM-DD') }}</div>
            </div>
            <span class="aml-tag" style="background:#F1EFE8;color:#5F5E5A;margin-right:8px">{{ comment.type }}</span>
            <template v-if="isOwnComment(comment)">
              <button class="aml-icon-btn mr-1" @click="openCommentDialog(comment)" title="Edit">
                <v-icon x-small color="#888">mdi-pencil-outline</v-icon>
              </button>
              <button class="aml-icon-btn" @click="confirmDeleteComment(comment)" title="Delete">
                <v-icon x-small color="#E24B4A">mdi-delete-outline</v-icon>
              </button>
            </template>
          </div>
          <div class="aml-comment-body">{{ comment.message }}</div>
        </div>
      </div>

      <!-- ══════════════════════════════════════
           TAB 3 — Stages
      ══════════════════════════════════════ -->
      <div v-show="tab === 3" class="aml-tab-content">

        <v-alert v-if="!orderedStages.length" type="info" outlined dense class="mb-4">
          No monitoring cycle actions have been seeded for this case. The matching cycle may not be active, or none was configured for this report type ({{ amlCase.reportType }}).
        </v-alert>

        <div v-for="(stageGroup, stageIdx) in orderedStages" :key="stageGroup.stage" class="aml-stage-card mb-4" :class="{ 'aml-stage-blocked': isStageBlocked(stageIdx), 'aml-stage-active': stageIdx === firstUnblockedIncompleteIdx && !isStageBlocked(stageIdx) }">

          <!-- Stage header -->
          <div class="aml-stage-head">
            <div class="aml-stage-num" :style="stageNumStyle(stageGroup, stageIdx)">{{ stageIdx + 1 }}</div>
            <span class="aml-panel-title ml-2">{{ stageGroup.stage }}</span>
            <v-icon v-if="isStageBlocked(stageIdx)" small color="#bbb" class="ml-1">mdi-lock-outline</v-icon>
            <v-spacer />
            <span class="aml-tag mr-2" :style="stageTagStyle(stageGroup, stageIdx)">{{ stageStatusLabel(stageGroup, stageIdx) }}</span>
            <span class="aml-label">{{ stageGroup.doneCount }}/{{ stageGroup.visibleActions.length }}</span>
          </div>

          <!-- Blocked notice -->
          <v-alert v-if="isStageBlocked(stageIdx)" type="warning" dense outlined class="ma-3" icon="mdi-lock-outline">
            Complete all required actions in <strong>{{ orderedStages[stageIdx - 1].stage }}</strong> before working on this stage.
          </v-alert>

          <template v-else>
            <div class="aml-divider" />
            <v-alert v-if="stageGroup.isEmptyStage" type="info" dense outlined class="ma-3" icon="mdi-playlist-plus">
              No actions configured for this stage. Use <strong>Add action</strong> to add manually.
            </v-alert>

            <div v-else>
              <div v-for="action in stageGroup.visibleActions" :key="action.id" class="aml-action-row" :class="{ 'aml-action-done': action.complete, 'aml-action-waived': action.waived }">
                <v-icon small :color="actionIconColor(action)" class="mr-2 flex-shrink-0">{{ actionIcon(action) }}</v-icon>
                <div class="flex-grow-1 min-width-0">
                  <div class="d-flex align-center flex-wrap" style="gap:4px">
                    <span class="aml-value" :class="{ 'text-decoration-line-through aml-label': action.complete || action.waived }">{{ action.action }}</span>
                    <span class="aml-tag" :style="actionMeta(action).isRequired ? 'background:#FCEBEB;color:#A32D2D' : 'background:#F1EFE8;color:#5F5E5A'">
                      {{ actionMeta(action).isRequired ? 'required' : 'optional' }}
                    </span>
                    <span v-if="action.waived" class="aml-tag" style="background:#FAEEDA;color:#854F0B">waived</span>
                  </div>
                  <div v-if="actionDescription(action)" class="aml-label mt-1">{{ actionDescription(action) }}</div>
                  <div class="aml-label">{{ assigneeName(action.user) }} · {{ formatDate(action.dateCreated, 'YYYY-MM-DD') }}</div>
                </div>
                <div class="d-flex align-center ml-2" style="gap:4px;flex-shrink:0">
                  <!-- Action buttons only available to the assigned officer, and only while the case is open -->
                  <template v-if="canModifyStages">
                  <button v-if="!action.waived" class="aml-action-btn" :class="action.complete ? 'aml-action-btn--ghost' : 'aml-action-btn--primary'" :disabled="togglingAction === action.id" @click="toggleAction(action)">
                    {{ action.complete ? 'Undo' : 'Mark done' }}
                  </button>
                  <button v-if="!action.complete && !action.waived && !actionMeta(action).isRequired" class="aml-action-btn aml-action-btn--warn" :disabled="waivingAction === action.id" @click="openWaiveDialog(action)">Waive</button>
                  <button v-if="action.waived" class="aml-action-btn aml-action-btn--ghost" :disabled="waivingAction === action.id" @click="unwaiveAction(action)">Unwaive</button>
                  </template>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- All done banner -->
        <v-alert v-if="overallProgress.allDone && !isClosed" type="success" outlined class="mt-4" icon="mdi-check-circle-outline">
          <div class="d-flex align-center justify-space-between flex-wrap" style="gap:8px">
            <div>
              <div class="body-2 font-weight-medium">All stages complete</div>
              <div class="caption">Every required action has been completed or waived. You can now close this case.</div>
            </div>
            <button v-if="isAssigned" class="aml-hdr-btn" style="background:#EAF3DE;color:#3B6D11;border-color:#3B6D11" @click="closePromptDialog = true; closePromptDismissed = false">
              <v-icon x-small left>mdi-lock-check-outline</v-icon>Close case
            </button>
          </div>
        </v-alert>

        <v-alert v-else-if="!overallProgress.allDone && !isClosed && overallProgress.total > 0" type="info" outlined dense class="mt-4" icon="mdi-information-outline">
          <span class="caption">{{ overallProgress.total - overallProgress.done }} action{{ (overallProgress.total - overallProgress.done) !== 1 ? 's' : '' }} still pending.</span>
        </v-alert>
      </div>

      <!-- ══════════════════════════════════════
           TAB 4 — Activity
      ══════════════════════════════════════ -->
      <div v-show="tab === 4" class="aml-tab-content">
        <AmlActivityFeed :case-id="caseId" title="Case activity" :compact="false" />
      </div>

    </div>

    <!-- Link Flagged Attempt Picker -->
    <v-dialog v-model="attemptPickerDialog" max-width="600" persistent>
      <v-card>
        <v-card-title><v-icon left>mdi-flag-plus</v-icon>Link Flagged Attempt<v-spacer /><v-btn icon @click="attemptPickerDialog = false"><v-icon>mdi-close</v-icon></v-btn></v-card-title>
        <v-card-text>
          <div v-if="loadingAttempts" class="text-center py-4"><v-progress-circular indeterminate color="primary" size="32" /></div>
          <div v-else-if="!availableAttempts.length" class="caption grey--text text-center py-4">No unlinked flagged attempts found for this account.</div>
          <v-list v-else dense>
            <v-list-item v-for="a in availableAttempts" :key="a.id" :class="selectedAttemptId === a.id ? 'primary lighten-5' : ''" style="cursor:pointer;border-radius:4px" class="mb-1" @click="selectedAttemptId = a.id">
              <v-list-item-action class="mr-2">
                <v-icon :color="selectedAttemptId === a.id ? 'primary' : 'grey'">{{ selectedAttemptId === a.id ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}</v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title class="body-2">{{ a.suspicionReason }}</v-list-item-title>
                <v-list-item-subtitle class="caption"><span style="font-family:monospace">{{ attemptRef(a) }}</span> &bull; {{ a.currency }} {{ a.amount ? Number(a.amount).toLocaleString() : '—' }} &bull; {{ a.transactionDate }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action><span class="caption grey--text">{{ formatDate(a.dateCreated, 'YYYY-MM-DD') }}</span></v-list-item-action>
            </v-list-item>
          </v-list>
          <v-text-field v-if="availableAttempts.length || selectedAttemptId" v-model="attemptLinkReason" label="Reason (optional)" dense outlined class="mt-3" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="attemptPickerDialog = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!selectedAttemptId" :loading="txnSaving" @click="submitLinkAttempt">Link</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Link by raw ID -->
    <v-dialog v-model="txnDialog" max-width="480" persistent>
      <v-card>
        <v-card-title><v-icon left>mdi-link-plus</v-icon>Link transaction by ID<v-spacer /><v-btn icon @click="txnDialog = false"><v-icon>mdi-close</v-icon></v-btn></v-card-title>
        <v-card-text>
          <v-form ref="txnForm">
            <v-text-field v-model="txnForm.transactionId" label="Transaction ID" dense outlined class="mb-2" />
            <v-text-field v-model="txnForm.transactionDate" label="Date" type="date" dense outlined class="mb-2" />
            <v-text-field v-model="txnForm.reason" label="Reason" dense outlined />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="txnDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="txnSaving" @click="submitAddTxn">Link</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Unlink Confirmation -->
    <v-dialog v-model="removeTxnDialog" max-width="420">
      <v-card>
        <v-card-title>Unlink transaction?</v-card-title>
        <v-card-text>This removes the link from the case. The underlying record will not be deleted.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="removeTxnDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="removingTxn" @click="submitRemoveTxn">Unlink</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Waive Action Dialog -->
    <v-dialog v-model="waiveDialog" max-width="440">
      <v-card>
        <v-card-title>Waive action?</v-card-title>
        <v-card-text>
          <div class="body-2 mb-3">Waiving <strong>{{ waiveTarget && waiveTarget.action }}</strong> means it will be skipped. A waiver reason is required.</div>
          <v-textarea v-model="waiveReason" label="Waiver reason *" rows="3" dense outlined autofocus />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="waiveDialog = false">Cancel</v-btn>
          <v-btn color="orange darken-2" dark :disabled="!waiveReason.trim()" :loading="waivingAction === (waiveTarget && waiveTarget.id)" @click="submitWaive">Waive action</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Comment Dialog (add / edit) -->
    <v-dialog v-model="commentDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon left color="primary">mdi-comment-outline</v-icon>
            <span class="subtitle-1 font-weight-medium">{{ editingComment ? 'Edit comment' : 'Add comment' }}</span>
          </div>
          <v-btn icon @click="closeCommentDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-form ref="commentForm">
            <v-select v-if="!editingComment" v-model="commentForm.type" :items="commentTypeOptions" label="Comment type" dense outlined prepend-inner-icon="mdi-tag-outline" class="mb-3" />
            <v-textarea v-model="commentForm.message" label="Message *" rows="4" dense outlined prepend-inner-icon="mdi-text" :rules="[v => !!v || 'A message is required']" counter="1000" maxlength="1000" auto-grow />
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeCommentDialog">Cancel</v-btn>
          <v-btn depressed color="primary" :loading="commentSaving" @click="submitComment">
            <v-icon left small>{{ editingComment ? 'mdi-content-save-outline' : 'mdi-comment-plus-outline' }}</v-icon>
            {{ editingComment ? 'Save changes' : 'Add comment' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Comment Confirm -->
    <v-dialog v-model="deleteCommentDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="error">mdi-delete-outline</v-icon>
          <span class="subtitle-1 font-weight-medium">Delete comment?</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 body-2">This will permanently delete your comment. This action cannot be undone.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteCommentDialog = false">Cancel</v-btn>
          <v-btn depressed color="error" :loading="deletingComment" @click="submitDeleteComment">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Record Additional Action Dialog -->
    <v-dialog v-model="actionDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon left color="primary">mdi-plus-circle-outline</v-icon>
            <span class="subtitle-1 font-weight-medium">Record additional action</span>
          </div>
          <v-btn icon @click="actionDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-form ref="actionForm">
            <v-select v-model="actionForm.stage" :items="existingStageNames" label="Stage *" dense outlined prepend-inner-icon="mdi-progress-clock" :rules="[v => !!v || 'Required']" hint="Select one of the case's existing stages" persistent-hint class="mb-3" />
            <v-text-field v-model="actionForm.action" label="Action name *" dense outlined prepend-inner-icon="mdi-clipboard-check-outline" :rules="[v => !!v || 'Required']" class="mb-3" />
            <v-textarea v-model="actionForm.comment" label="Notes / description" rows="2" dense outlined prepend-inner-icon="mdi-text" class="mb-2" />
            <v-checkbox v-model="actionForm.complete" label="Mark as complete immediately" dense hide-details color="primary" />
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="actionDialog = false">Cancel</v-btn>
          <v-btn depressed color="primary" :loading="actionSaving" @click="submitAction">
            <v-icon left small>mdi-plus</v-icon>Add action
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Close Case Prompt -->
    <v-dialog v-model="closePromptDialog" max-width="440" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="success">mdi-lock-check-outline</v-icon>
          <span class="subtitle-1 font-weight-medium">Close this case?</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 body-2">All stages and actions for <strong>{{ amlCase && amlCase.caseReference }}</strong> have been completed. Would you like to proceed with closing the case?</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closePromptDialog = false; closePromptDismissed = true">Not yet</v-btn>
          <v-btn depressed color="success" @click="openCloseDialog"><v-icon left small>mdi-check</v-icon>Yes, close it</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Close Case Form Dialog -->
    <v-dialog v-model="closeDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="error">mdi-lock-outline</v-icon>
          <span class="subtitle-1 font-weight-medium">Close case — {{ amlCase && amlCase.caseReference }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-form ref="closeForm">
            <v-select v-model="closeForm.status" :items="closeStatusOptions" item-text="text" item-value="value" label="Outcome *" dense outlined class="mb-3" prepend-inner-icon="mdi-list-status" :rules="[v => !!v || 'Required']" />
            <v-text-field v-model="closeForm.closeReason" label="Reason *" dense outlined class="mb-3" prepend-inner-icon="mdi-text-short" :rules="[v => !!v || 'Required']" />
            <v-textarea v-model="closeForm.closeComment" label="Additional comments" rows="3" dense outlined prepend-inner-icon="mdi-comment-outline" />
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDialog = false">Cancel</v-btn>
          <v-btn depressed color="error" :loading="closing" @click="submitClose">
            <v-icon left small>mdi-lock</v-icon>Close case
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';
import PageMixin   from '@/mixins/page.mixin';
import FormatMixin from '@/mixins/format.mixin';
import AmlMixin    from '@/mixins/aml.mixin';
import { AmlCaseService } from '@/modules/amlpro/services/aml-case.service';
import AmlActivityFeed from './AmlActivityFeed.vue';

const svc = new AmlCaseService();

function parseActionMeta(comment) {
  const defaults = { stageOrder: 999, actionOrder: 999, isRequired: false, empty: false };
  if (!comment) return defaults;
  const match = comment.match(/^__meta:([^_]+)__/);
  if (!match) return defaults;
  const parts = match[1].split(';');
  const meta  = { ...defaults };
  for (const p of parts) {
    const [k, v] = p.split('=');
    if (k === 'stageOrder')  meta.stageOrder  = parseInt(v, 10);
    if (k === 'actionOrder') meta.actionOrder = parseInt(v, 10);
    if (k === 'isRequired')  meta.isRequired  = v === 'true';
    if (k === 'empty')       meta.empty       = v === 'true';
  }
  return meta;
}

function stripMeta(comment) {
  if (!comment) return '';
  return comment.replace(/^__meta:[^_]+__/, '').trim();
}

export default {
  name: 'AmlCaseDetail',
  mixins: [PageMixin, FormatMixin, AmlMixin],
  components: { AmlActivityFeed },
  props: { id: { type: String, default: null } },

  data() {
    return {
      tab: 0,
      amlCase: null,
      loading: false,
      saving: false,
      closing: false,
      txnDialog: false,
      txnSaving: false,
      removeTxnDialog: false,
      removingTxn: false,
      selectedTxn: null,
      togglingAction: null,
      commentSaving: false,
      actionSaving: false,

      commentDialog: false,
      editingComment: null,
      deleteCommentDialog: false,
      deletingComment: false,
      deletingCommentTarget: null,

      actionDialog: false,

      attemptPickerDialog: false,
      loadingAttempts: false,
      availableAttempts: [],
      selectedAttemptId: null,
      attemptLinkReason: '',

      closePromptDialog: false,
      closeDialog: false,
      closePromptDismissed: false,

      waiveDialog: false,
      waiveTarget: null,
      waiveReason: '',
      waivingAction: null,

      txnDrawer: false,
      txnDrawerItem: null,

      updateForm: { status: '', priority: '', reportStage: '', summary: '' },
      closeForm:  { status: '', closeReason: '', closeComment: '' },
      txnForm:    { transactionId: '', transactionDate: '', reason: '' },
      commentForm:{ type: 'NOTE', message: '', evidenceUrls: [] },
      actionForm: { action: '', stage: '', comment: '', complete: false, reportStatus: 0 },

      statusOptions: ['OPEN','CLOSED_INVESTIGATION_COMPLETED','CLOSED_NO_ACTION','CLOSED_REPORT_FILED'],
      closeStatusOptions: [
        { text: 'Investigation completed', value: 'CLOSED_INVESTIGATION_COMPLETED' },
        { text: 'No action taken',         value: 'CLOSED_NO_ACTION' },
        { text: 'Report filed',            value: 'CLOSED_REPORT_FILED' },
      ],
      priorityOptions:    ['HIGH','MEDIUM','LOW'],
      commentTypeOptions: ['NOTE','EVIDENCE','ESCALATION','SAR_RELATED'],
    };
  },

  computed: {
    ...mapState('authModule', { loggedInUser: 'user' }),

    caseId()     { return this.id || this.$route.params.id; },

    linkedTxnRows() {
      const links = (this.amlCase && this.amlCase.caseTransactions) || [];
      return links.map(link => ({ link, v: this.txnView(link) }));
    },

    txnDrawerView() {
      return this.txnDrawerItem ? this.txnView(this.txnDrawerItem) : null;
    },

    // True only when the logged-in user is the officer assigned to this case.
    // All mutative actions (toggle/waive actions, add action, close case,
    // link/unlink transactions) are disabled when false.
    // Comments are always available to all users — see isOwnComment() for
    // edit/delete permission on individual comments.
    isAssigned() {
      if (!this.amlCase || !this.loggedInUser) return false;
      return this.amlCase.assignedToUserId === this.loggedInUser.id;
    },

    isClosed()   { return this.isClosedStatus(this.amlCase?.status); },

    // Stages/actions may only be modified by the assigned officer, and only
    // while the case remains open. Once closed, the case record is locked
    // for everyone, including the assignee.
    canModifyStages() {
      return this.isAssigned && !this.isClosed;
    },

    // Linking/unlinking transactions & flagged attempts follows the same
    // rule as stages: assigned officer only, and only while the case is open.
    canModifyTransactions() {
      return this.isAssigned && !this.isClosed;
    },

    tabDefs() {
      return [
        { label: 'Overview' },
        { label: 'Transactions' },
        { label: 'Comments' },
        { label: 'Stages', badge: `${this.stageProgress.done}/${this.stageProgress.total}`, badgeDone: this.stageProgress.allDone },
        { label: 'Activity' },
      ];
    },

    accountPerson() {
      const rows = this.amlCase?.account?.person;
      if (!rows?.length) return null;
      const primary = rows.find(r => r.personClass === 'PRIMARY' || r.personClass === 'MEMBER');
      return (primary ?? rows[0])?.person ?? null;
    },
    accountData()          { return this.amlCase?.account ?? {}; },
    personContact()        { return Array.isArray(this.accountPerson?.contact) ? this.accountPerson.contact : []; },
    personPhone()          { const c = this.personContact.find(c => ['MOBILE','CELL','PHONE'].includes(c.type)); return c?.number ?? c?.value ?? null; },
    personAddress() {
      const addr = this.accountPerson?.livingAddress;
      if (!addr) return null;
      return [addr.street, addr.city, addr.province, addr.country].filter(Boolean).join(', ') || null;
    },
    personIds()            { return Array.isArray(this.accountPerson?.identifications) ? this.accountPerson.identifications : []; },
    personEmployer()       { const e = this.accountPerson?.employer ?? []; return Array.isArray(e) ? (e[0] ?? null) : null; },
    personSourceOfFunds()  { const s = this.accountPerson?.sourceOfFunds ?? []; return Array.isArray(s) ? s : []; },
    personSourceOfWealth() { const s = this.accountPerson?.sourceOfWealth ?? []; return Array.isArray(s) ? s : []; },
    personKyc()            { return this.accountPerson?.kycScore ?? null; },
    personWatchlistMatches() {
      const wl = this.accountPerson?.watchListMatches ?? [];
      return Array.isArray(wl) ? wl.filter(m => m && m.status !== 'NO_MATCH') : [];
    },

    orderedStages() {
      if (!this.amlCase?.actions?.length) return [];
      const stageMap = {};
      for (const action of this.amlCase.actions) {
        const meta = parseActionMeta(action.comment);
        const key  = action.stage;
        if (!stageMap[key]) stageMap[key] = { stage: key, stageOrder: meta.stageOrder, actions: [] };
        if (meta.stageOrder < stageMap[key].stageOrder) stageMap[key].stageOrder = meta.stageOrder;
        stageMap[key].actions.push({ ...action, _meta: meta });
      }
      const stages = Object.values(stageMap).sort((a, b) => a.stageOrder - b.stageOrder);
      for (const stage of stages) {
        stage.actions.sort((a, b) => a._meta.actionOrder - b._meta.actionOrder);
        stage.isEmptyStage   = stage.actions.every(a => a._meta.empty);
        stage.visibleActions = stage.actions.filter(a => !a._meta.empty);
        stage.doneCount      = stage.isEmptyStage ? 0 : stage.visibleActions.filter(a => a.complete || a.waived).length;
        stage.requiredDone   = stage.isEmptyStage ? true : stage.visibleActions.filter(a => a._meta.isRequired).every(a => a.complete || a.waived);
        stage.allDone        = stage.isEmptyStage ? true : stage.visibleActions.every(a => a.complete || a.waived);
      }
      return stages;
    },

    overallProgress() {
      const all  = this.orderedStages.flatMap(s => s.visibleActions || s.actions);
      const done = all.filter(a => a.complete || a.waived).length;
      return { done, total: all.length, allDone: done === all.length && all.length > 0 };
    },

    accountTrends() {
      const account = this.amlCase?.account;
      const trends  = account?.trends ?? account?.accountInfo?.trends ?? [];
      return Array.isArray(trends) ? trends : [];
    },

    stageProgress() {
      const total = this.orderedStages.length;
      const done  = this.orderedStages.filter((s, i) => !s.isEmptyStage && !this.isStageBlocked(i) && s.allDone).length;
      return { done, total, allDone: done === total && total > 0 };
    },

    existingStageNames() { return this.orderedStages.map(s => s.stage); },

    firstUnblockedIncompleteIdx() {
      return this.orderedStages.findIndex((s, i) => !this.isStageBlocked(i) && !s.allDone);
    },
  },

  mounted() { this.loadCase(); },

  methods: {
    // ── Style helpers for new design ─────────────────────────────────────────
    closedPillStyle(s) {
      const m = { CLOSED_INVESTIGATION_COMPLETED: 'background:#EAF3DE;color:#3B6D11', CLOSED_NO_ACTION: 'background:#F1EFE8;color:#5F5E5A', CLOSED_REPORT_FILED: 'background:#EEEDFE;color:#534AB7' };
      return m[s] || 'background:#F1EFE8;color:#5F5E5A';
    },
    priorityPillStyle(p) {
      const m = { HIGH: 'background:#FCEBEB;color:#A32D2D', MEDIUM: 'background:#FAEEDA;color:#854F0B', LOW: 'background:#EAF3DE;color:#3B6D11' };
      return m[p] || 'background:#F1EFE8;color:#5F5E5A';
    },
    riskTagStyle(r) {
      const m = { HIGH: 'background:#FCEBEB;color:#A32D2D', MEDIUM: 'background:#FAEEDA;color:#854F0B', LOW: 'background:#EAF3DE;color:#3B6D11' };
      const key = (r || '').toUpperCase();
      return m[key] || 'background:#F1EFE8;color:#5F5E5A';
    },
    accountStatusStyle(s) {
      if (s === 'ACTIVE')  return 'background:#EAF3DE;color:#3B6D11';
      if (s === 'DORMANT') return 'background:#FAEEDA;color:#854F0B';
      return 'background:#F1EFE8;color:#5F5E5A';
    },
    kycColor(level) {
      if (!level) return '#888';
      const l = level.toLowerCase();
      if (l === 'high')   return '#E24B4A';
      if (l === 'medium') return '#EF9F27';
      return '#1D9E75';
    },
    stageNumStyle(stageGroup, stageIdx) {
      if (this.isStageBlocked(stageIdx)) return 'background:#e0e0e0;color:#aaa';
      if (stageGroup.allDone)             return 'background:#EAF3DE;color:#3B6D11';
      if (stageIdx === this.firstUnblockedIncompleteIdx) return 'background:#E6F1FB;color:#185FA5';
      return 'background:#F1EFE8;color:#888';
    },
    stageTagStyle(stageGroup, stageIdx) {
      if (this.isStageBlocked(stageIdx)) return 'background:#F1EFE8;color:#aaa';
      if (stageGroup.allDone)             return 'background:#EAF3DE;color:#3B6D11';
      if (stageGroup.doneCount > 0)       return 'background:#FAEEDA;color:#854F0B';
      return 'background:#E6F1FB;color:#185FA5';
    },

    async loadCase() {
      this.loading = true;
      try {
        const res = await svc.fetchOne(this.caseId);
        const raw = res.data ?? res;
        if (raw.actions) {
          raw.actions = raw.actions.map(a => ({ ...a, waived: stripMeta(a.comment).startsWith('WAIVED:') }));
        }
        this.amlCase    = raw;
        this.updateForm = { status: raw.status, priority: raw.priority, reportStage: raw.reportStage, summary: raw.summary };
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to load case' });
      } finally {
        this.loading = false;
      }
    },

    openTxnDrawer(item) {
      this.txnDrawerItem = item;
      this.txnDrawer = true;
    },

    txnView(link) {
      if (!link) return null;

      if (link.transaction) {
        const t = link.transaction;
        return {
          kind:             'transaction',
          code:             t.transactionCode || t.transactionId,
          date:             t.transactionDate,
          amount:           t.transactionAmount,
          currency:         null,
          type:             t.transactionType === 'CREDIT' ? 'CREDIT' : 'DEBIT',
          method:           t.transactionMethod,
          location:         t.transactionLocation,
          description:      t.description,
          balanceAfter:     t.balanceAfter,
          subAccountNumber: t.subAccountNumber,
          tellerId:         t.tellerId,
        };
      }

      if (link.attempt) {
        const a  = link.attempt;
        const up = (a.transactionType || '').toUpperCase();
        return {
          kind:              'attempt',
          code:              this.attemptRef(a),
          date:              a.transactionDate,
          amount:            a.amount != null ? a.amount : a.fxAmount,
          currency:          a.currency,
          type:              (up === 'CREDIT' || up === 'DEBIT') ? up : (a.transactionType || null),
          method:            a.transactionMethod || a.passThroughType,
          location:          [a.bank, a.city, a.country].filter(Boolean).join(' • ') || null,
          description:       a.transactionDescription,
          balanceAfter:      null,
          chequeNumber:      a.chequeNumber,
          sourceOfFunds:     a.sourceOfFunds,
          suspicionReason:   a.suspicionReason,
          suspicionCategory: a.suspicionCategory,
          flaggedBy:         a.flaggedBy,
          customerName:      [a.firstName, a.lastName].filter(Boolean).join(' ') || null,
          idNumber:          a.idNumber,
          idType:            a.idType,
          trn:               a.trn,
          accountNumber:     a.accountNumber || a.mainAccountNumber,
          cellNumber:        a.cellNumber,
          senderName:        [a.senderFirstName, a.senderLastName].filter(Boolean).join(' ') || null,
          senderAccountNumber:   a.senderAccountNumber,
          receiverName:      [a.receiverFirstName, a.receiverLastName].filter(Boolean).join(' ') || null,
          receiverAccountNumber: a.receiverAccountNumber,
        };
      }

      return {
        kind: link.transactionId ? 'transaction' : 'attempt',
        code: link.transactionId || (link.attemptId ? 'FA-' + link.attemptId.slice(0, 8).toUpperCase() : null),
        date: link.transactionDate,
        amount: null, currency: null, type: null, method: null,
        location: null, description: null, balanceAfter: null,
      };
    },

    attemptRef(a) {
      if (!a) return null;
      if (a.attemptNo != null) return 'FA-' + String(a.attemptNo).padStart(6, '0');
      return a.id ? 'FA-' + a.id.slice(0, 8).toUpperCase() : null;
    },

    isStageBlocked(stageIdx) {
      if (stageIdx === 0) return false;
      for (let i = 0; i < stageIdx; i++) { if (!this.orderedStages[i].requiredDone) return true; }
      return false;
    },

    stageStatusLabel(stageGroup, stageIdx) {
      if (this.isStageBlocked(stageIdx)) return 'Locked';
      if (stageGroup.allDone)             return 'Complete';
      if (stageGroup.doneCount > 0)       return 'In progress';
      return 'Pending';
    },

    actionMeta(action)        { return action._meta || parseActionMeta(action.comment); },
    actionDescription(action) { return stripMeta(action.comment).replace(/^WAIVED:\s*/, ''); },

    actionIcon(action) {
      if (action.waived)   return 'mdi-minus-circle-outline';
      if (action.complete) return 'mdi-check-circle';
      return 'mdi-circle-outline';
    },

    actionIconColor(action) {
      if (action.waived)                      return '#EF9F27';
      if (action.complete)                    return '#1D9E75';
      if (this.actionMeta(action).isRequired) return '#E24B4A';
      return '#ccc';
    },

    async toggleAction(action) {
      if (!this.canModifyStages) return;
      this.togglingAction = action.id;
      try {
        const res    = await svc.updateAction(this.caseId, action.id, { complete: !action.complete });
        const result = res.data ?? res;
        const idx    = this.amlCase.actions.findIndex(a => a.id === action.id);
        if (idx !== -1) this.$set(this.amlCase.actions, idx, { ...this.amlCase.actions[idx], ...result });
        this.showSnack({ color: 'success', title: 'Updated', message: `Action marked ${result.complete ? 'complete' : 'incomplete'}` });
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to update action' });
      } finally {
        this.togglingAction = null;
      }
    },

    openWaiveDialog(action) {
      if (!this.canModifyStages) return;
      this.waiveTarget = action; this.waiveReason = ''; this.waiveDialog = true;
    },

    async submitWaive() {
      if (!this.canModifyStages) return;
      if (!this.waiveReason.trim()) return;
      this.waivingAction = this.waiveTarget.id;
      try {
        const existingMeta = this.waiveTarget.comment?.match(/^__meta:[^_]+__/) ? this.waiveTarget.comment.match(/^(__meta:[^_]+__)/)[1] : '';
        const newComment   = `${existingMeta}WAIVED: ${this.waiveReason.trim()}`;
        const res    = await svc.updateAction(this.caseId, this.waiveTarget.id, { complete: false, comment: newComment });
        const result = res.data ?? res;
        const idx    = this.amlCase.actions.findIndex(a => a.id === this.waiveTarget.id);
        if (idx !== -1) this.$set(this.amlCase.actions, idx, { ...this.amlCase.actions[idx], ...result, waived: true });
        this.waiveDialog = false;
        this.showSnack({ color: 'success', title: 'Waived', message: 'Action waived' });
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to waive action' });
      } finally {
        this.waivingAction = null;
        this.waiveTarget   = null;
      }
    },

    async unwaiveAction(action) {
      if (!this.canModifyStages) return;
      this.waivingAction = action.id;
      try {
        const metaMatch  = action.comment?.match(/^(__meta:[^_]+__)/);
        const newComment = metaMatch ? metaMatch[1] : '';
        const res    = await svc.updateAction(this.caseId, action.id, { complete: false, comment: newComment || undefined });
        const result = res.data ?? res;
        const idx    = this.amlCase.actions.findIndex(a => a.id === action.id);
        if (idx !== -1) this.$set(this.amlCase.actions, idx, { ...this.amlCase.actions[idx], ...result, waived: false });
        this.showSnack({ color: 'success', title: 'Unwaived', message: 'Waiver removed' });
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to unwaive action' });
      } finally {
        this.waivingAction = null;
      }
    },

    checkForCompletion() {
      if (this.overallProgress.allDone && !this.isClosed && !this.closePromptDismissed) {
        this.$nextTick(() => { this.closePromptDialog = true; });
      }
    },

    async submitUpdate() {
      if (!this.$refs.updateForm.validate()) return;
      this.saving = true;
      try {
        const res     = await svc.update(this.caseId, this.updateForm);
        const updated = res.data ?? res;
        Object.assign(this.amlCase, updated.updated ?? updated);
        this.showSnack({ color: 'success', title: 'Updated', message: 'Case updated successfully' });
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: e?.response?.data?.message ?? 'Update failed' });
      } finally {
        this.saving = false;
      }
    },

    async submitClose() {
      if (!this.$refs.closeForm.validate()) return;
      this.closing = true;
      try {
        const res    = await svc.close(this.caseId, this.closeForm);
        const result = res.data ?? res;
        Object.assign(this.amlCase, result);
        this.closeDialog       = false;
        this.closePromptDialog = false;
        this.showSnack({ color: 'success', title: 'Case closed', message: `${this.amlCase.caseReference} closed as ${this.formatStatus(result.status)}` });
        setTimeout(() => this.$router.push({ name: 'aml-case-dashboard' }), 1200);
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: e?.response?.data?.message ?? 'Close failed' });
      } finally {
        this.closing = false;
      }
    },

    openCloseDialog() { this.closePromptDialog = false; this.closeDialog = true; },

    async openAttemptPickerDialog() {
      if (!this.canModifyTransactions) return;
      this.attemptPickerDialog = true;
      this.selectedAttemptId   = null;
      this.attemptLinkReason   = '';
      this.loadingAttempts     = true;
      try {
        const accountId = this.amlCase?.account?.id;
        if (!accountId) return;
        const res       = await svc.getFlaggedAttemptsByAccount(accountId);
        const all       = res.data ?? res;
        const linkedIds = new Set((this.amlCase.caseTransactions || []).filter(t => t.attemptId).map(t => t.attemptId));
        this.availableAttempts = all.filter(a => !linkedIds.has(a.id));
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to load flagged attempts' });
      } finally {
        this.loadingAttempts = false;
      }
    },

    async submitLinkAttempt() {
      if (!this.canModifyTransactions) return;
      if (!this.selectedAttemptId) return;
      this.txnSaving = true;
      try {
        const res = await svc.addTransaction(this.caseId, { attemptId: this.selectedAttemptId, reason: this.attemptLinkReason || undefined });
        this.amlCase.caseTransactions.unshift(res.data ?? res);
        this.attemptPickerDialog = false;
        this.selectedAttemptId   = null;
        this.showSnack({ color: 'success', title: 'Linked', message: 'Flagged attempt linked' });
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: e?.response?.data?.message ?? 'Failed to link' });
      } finally {
        this.txnSaving = false;
      }
    },

    async submitAddTxn() {
      if (!this.canModifyTransactions) return;
      if (!this.txnForm.transactionId) {
        this.showSnack({ color: 'warning', title: 'Required', message: 'Provide a Transaction ID' });
        return;
      }
      this.txnSaving = true;
      try {
        const payload = { ...this.txnForm };
        if (!payload.transactionDate) delete payload.transactionDate;
        const res = await svc.addTransaction(this.caseId, payload);
        this.amlCase.caseTransactions.unshift(res.data ?? res);
        this.txnDialog = false;
        this.txnForm   = { transactionId: '', transactionDate: '', reason: '' };
        this.showSnack({ color: 'success', title: 'Linked', message: 'Transaction linked' });
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: e?.response?.data?.message ?? 'Failed to link' });
      } finally {
        this.txnSaving = false;
      }
    },

    confirmRemoveTxn(item) {
      if (!this.canModifyTransactions) return;
      this.selectedTxn = item; this.removeTxnDialog = true;
    },

    async submitRemoveTxn() {
      if (!this.canModifyTransactions) return;
      this.removingTxn = true;
      try {
        await svc.removeTransaction(this.caseId, this.selectedTxn.id);
        this.amlCase.caseTransactions = this.amlCase.caseTransactions.filter(t => t.id !== this.selectedTxn.id);
        this.removeTxnDialog = false;
        this.selectedTxn     = null;
        this.showSnack({ color: 'success', title: 'Unlinked', message: 'Transaction unlinked' });
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to unlink' });
      } finally {
        this.removingTxn = false;
      }
    },

    openCommentDialog(comment = null) {
      this.editingComment = comment;
      this.commentForm    = comment ? { type: comment.type, message: comment.message, evidenceUrls: comment.evidenceUrls ?? [] } : { type: 'NOTE', message: '', evidenceUrls: [] };
      this.commentDialog  = true;
    },

    closeCommentDialog() { this.commentDialog = false; this.editingComment = null; this.$refs.commentForm?.reset(); },

    async submitComment() {
      if (!this.$refs.commentForm.validate()) return;
      this.commentSaving = true;
      try {
        if (this.editingComment) {
          const res     = await svc.updateComment(this.caseId, this.editingComment.id, this.commentForm.message);
          const updated = res.data ?? res;
          const idx     = this.amlCase.comments.findIndex(c => c.id === this.editingComment.id);
          if (idx > -1) this.$set(this.amlCase.comments, idx, { ...this.amlCase.comments[idx], message: updated.message });
          this.showSnack({ color: 'success', title: 'Updated', message: 'Comment updated' });
        } else {
          const res = await svc.addComment(this.caseId, this.commentForm);
          this.amlCase.comments.unshift(res.data ?? res);
          this.showSnack({ color: 'success', title: 'Added', message: 'Comment saved' });
        }
        this.closeCommentDialog();
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to save comment' });
      } finally {
        this.commentSaving = false;
      }
    },

    confirmDeleteComment(comment) { this.deletingCommentTarget = comment; this.deleteCommentDialog = true; },

    async submitDeleteComment() {
      this.deletingComment = true;
      try {
        await svc.deleteComment(this.caseId, this.deletingCommentTarget.id);
        this.amlCase.comments      = this.amlCase.comments.filter(c => c.id !== this.deletingCommentTarget.id);
        this.deleteCommentDialog   = false;
        this.deletingCommentTarget = null;
        this.showSnack({ color: 'success', title: 'Deleted', message: 'Comment deleted' });
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'You can only delete your own comments' });
      } finally {
        this.deletingComment = false;
      }
    },

    isOwnComment(comment) { return comment.userId === this.loggedInUser?.id; },

    initials(user) {
      if (!user) return '?';
      const first = user.person?.firstName?.[0] ?? '';
      const last  = user.person?.lastName?.[0]  ?? '';
      return (first + last).toUpperCase() || (user.username?.[0] ?? '?').toUpperCase();
    },

    personInitials(person) {
      if (!person) return '?';
      return ((person.firstName?.[0] ?? '').toUpperCase() + (person.lastName?.[0] ?? '').toUpperCase()) || '?';
    },

    async submitAction() {
      if (!this.canModifyStages) return;
      if (!this.$refs.actionForm.validate()) return;
      this.actionSaving = true;
      try {
        const stageGroup = this.orderedStages.find(s => s.stage === this.actionForm.stage);
        const stageOrder = stageGroup ? stageGroup.stageOrder : 999;
        const metaPrefix = `__meta:stageOrder=${stageOrder};actionOrder=999;isRequired=false__`;
        const payload = { ...this.actionForm, comment: this.actionForm.comment ? `${metaPrefix}${this.actionForm.comment}` : metaPrefix };
        const res       = await svc.addAction(this.caseId, payload);
        const newAction = { ...(res.data ?? res), waived: false };
        this.amlCase.actions.push(newAction);
        this.actionForm  = { action: '', stage: '', comment: '', complete: false, reportStatus: 0 };
        this.actionDialog = false;
        this.$refs.actionForm?.reset();
        this.showSnack({ color: 'success', title: 'Action Added', message: 'Action added to stage' });
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to add action' });
      } finally {
        this.actionSaving = false;
      }
    },

    formatTrendName(name) {
      if (!name) return '';
      return name.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    },
    formatTrendValue(trend) {
      const v = Number(trend.value);
      if (isNaN(v)) return '—';
      if (trend.name === 'average number of deposits') return v.toFixed(1);
      return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatTrendChange(change) {
      const n = Number(change);
      if (isNaN(n)) return '0.00%';
      return (n >= 0 ? '+' : '') + n.toFixed(2) + '%';
    },
    trendChangeColor(change) { return Number(change) >= 0 ? 'success' : 'error'; },
    trendIcon(name) {
      const map = { 'average internal transfer deposits':'mdi-bank-transfer', 'average withdrawal amount':'mdi-cash-minus', 'average cheque deposit amount':'mdi-file-document-outline', 'average cash deposit amount':'mdi-cash-plus', 'average wire transfers amount':'mdi-bank', 'average number of deposits':'mdi-archive-arrow-down' };
      return map[name] || 'mdi-chart-bar';
    },
  },
};
</script>

<style scoped>
/* ── Root ── */
.aml-detail-root { font-family: inherit; }
.aml-detail-wrap { padding: 20px 24px 32px; }

/* ── Header ── */
.aml-detail-header { display:flex; align-items:center; margin-bottom:20px; flex-wrap:wrap; gap:8px; }
.aml-back-btn { width:34px; height:34px; border-radius:8px; border:0.5px solid #e0e0e0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background 0.12s; flex-shrink:0; }
.aml-back-btn:hover { background:#f5f5f5; }
.aml-case-ref { font-size:20px; font-weight:500; letter-spacing:-0.3px; }
.aml-case-sub { font-size:12px; color:#999; margin-top:2px; }
.aml-header-divider { width:1px; height:24px; background:#e0e0e0; margin:0 4px; align-self:center; }

/* ── Header buttons ── */
.aml-hdr-btn { font-size:12px; padding:5px 12px; border-radius:8px; border:0.5px solid #ddd; background:transparent; color:inherit; cursor:pointer; display:inline-flex; align-items:center; gap:4px; transition:background 0.12s; font-family:inherit; }
.aml-hdr-btn:hover { background:#f5f5f5; }
.aml-hdr-btn--primary { background:#185FA5; color:#fff; border-color:#185FA5; }
.aml-hdr-btn--primary:hover { background:#0C447C; }
.aml-hdr-btn--danger { background:#FCEBEB; color:#A32D2D; border-color:#F09595; }
.aml-hdr-btn--danger:hover { background:#F7C1C1; }

/* ── Read-only badge ── */
.aml-readonly-badge { display:inline-flex; align-items:center; gap:3px; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; background:#F1EFE8; color:#888; border:0.5px solid #ddd; }

/* ── Status pills (header) ── */
.aml-status-pill { display:inline-flex; align-items:center; gap:3px; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; }
.aml-pill-blue   { background:#E6F1FB; color:#185FA5; }

/* ── Tabs ── */
.aml-tabs-bar { display:flex; border-bottom:0.5px solid #e0e0e0; margin-bottom:20px; gap:0; overflow-x:auto; }
.aml-tab { font-size:13px; padding:10px 18px; border:none; background:transparent; color:#888; cursor:pointer; border-bottom:2px solid transparent; transition:all 0.15s; white-space:nowrap; display:inline-flex; align-items:center; gap:6px; font-family:inherit; }
.aml-tab:hover { color:var(--color-text-primary, #1a1a1a); }
.aml-tab--active { color:#185FA5; border-bottom-color:#185FA5; font-weight:500; }
.aml-tab-badge { font-size:10px; font-weight:700; padding:1px 6px; border-radius:10px; background:#F1EFE8; color:#888; }
.aml-tab-badge--done { background:#EAF3DE; color:#3B6D11; }

.aml-tab-content { }

/* ── Panels (cards) ── */
.aml-panel { border:0.5px solid #e0e0e0; border-radius:12px; overflow:hidden; background:white; height:100%; }
.aml-panel-head { display:flex; align-items:center; padding:12px 14px; border-bottom:0.5px solid #e0e0e0; gap:8px; }
.aml-panel-icon { width:28px; height:28px; border-radius:8px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.aml-panel-title { font-size:13px; font-weight:500; }

/* ── Person pill ── */
.aml-person-pill { display:flex; align-items:center; gap:10px; background:#EBF3FC; border-radius:10px; padding:10px 12px; }
.aml-person-avatar { width:36px; height:36px; border-radius:50%; background:#185FA5; color:white; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; font-family:monospace; flex-shrink:0; }
.aml-person-name { font-size:13px; font-weight:500; }
.aml-person-sub  { font-size:11px; color:#888; margin-top:2px; }

/* ── Account pill ── */
.aml-acct-pill { display:flex; align-items:center; background:#E1F5EE; border-radius:10px; padding:10px 12px; }

/* ── Field grid ── */
.aml-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:10px 16px; }
.aml-field  { }
.aml-label  { font-size:11px; color:#999; margin-bottom:2px; }
.aml-value  { font-size:12px; color:var(--color-text-primary, #1a1a1a); }
.aml-section-label { font-size:10px; font-weight:600; letter-spacing:.5px; text-transform:uppercase; color:#aaa; margin-bottom:6px; display:flex; align-items:center; gap:3px; }
.aml-divider { height:0.5px; background:#e0e0e0; }

/* ── Tags / pills ── */
.aml-tag { display:inline-block; padding:2px 8px; border-radius:20px; font-size:10px; font-weight:500; white-space:nowrap; }
.aml-tag-blue   { background:#E6F1FB; color:#185FA5; }
.aml-tag-purple { background:#EEEDFE; color:#534AB7; }

/* ── KYC bar ── */
.aml-kyc-track { height:6px; border-radius:4px; background:#eee; width:100px; overflow:hidden; }
.aml-kyc-bar   { height:100%; border-radius:4px; transition:width 0.5s; }

/* ── Trend cards ── */
.aml-trend-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
.aml-trend-card { border:0.5px solid #e0e0e0; border-radius:8px; padding:10px; }
.aml-trend-value { font-size:14px; font-weight:600; }

/* ── Table (transactions tab) ── */
.aml-table-wrap { overflow-x:auto; }
.aml-table { width:100%; border-collapse:collapse; font-size:12px; }
.aml-table thead tr { border-bottom:0.5px solid #e0e0e0; }
.aml-table th { padding:9px 14px; text-align:left; font-size:10px; font-weight:600; color:#aaa; letter-spacing:.5px; text-transform:uppercase; }
.aml-table-row { border-bottom:0.5px solid #e0e0e0; transition:background 0.1s; }
.aml-table-row:hover { background:#f7f9fc; }
.aml-table td { padding:10px 14px; }
.aml-empty-row { padding:32px 20px; text-align:center; color:#aaa; font-size:13px; }

/* ── Icon buttons ── */
.aml-icon-btn { width:26px; height:26px; border-radius:6px; border:0.5px solid #e0e0e0; background:transparent; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; padding:0; transition:background 0.1s; }
.aml-icon-btn:hover { background:#f0f0f0; }

/* ── Transaction table extras ── */
.aml-txn-row--clickable { cursor:pointer; }
.aml-truncate-cell { max-width:130px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

/* ── Transaction drawer ── */
.txn-drawer { display:flex; flex-direction:column; height:100%; font-family:inherit; }
.txn-drawer-header { display:flex; align-items:flex-start; justify-content:space-between; padding:16px 16px 12px; border-bottom:0.5px solid #e0e0e0; flex-shrink:0; }
.txn-drawer-title { font-size:15px; font-weight:600; }
.txn-drawer-sub { font-size:11px; color:#888; margin-top:2px; }
.txn-drawer-amount-block { padding:20px 16px; text-align:center; flex-shrink:0; }
.txn-credit-bg { background:#E1F5EE; }
.txn-debit-bg  { background:#FCEBEB; }
.txn-drawer-amount-label { font-size:11px; font-weight:600; letter-spacing:.5px; text-transform:uppercase; color:#777; margin-bottom:4px; }
.txn-drawer-amount-value { font-size:28px; font-weight:700; letter-spacing:-1px; }
.txn-drawer-balance { font-size:11px; color:#888; margin-top:6px; }
.txn-drawer-body { flex:1; overflow-y:auto; padding:16px; }
.txn-drawer-section-label { font-size:10px; font-weight:600; letter-spacing:.5px; text-transform:uppercase; color:#aaa; margin-bottom:8px; }
.txn-drawer-row { display:flex; justify-content:space-between; align-items:flex-start; padding:6px 0; border-bottom:0.5px solid #f5f5f5; gap:12px; }
.txn-drawer-row:last-child { border-bottom:none; }
.txn-drawer-field-label { font-size:11px; color:#999; flex-shrink:0; }
.txn-drawer-field-value { font-size:12px; color:#1a1a1a; text-align:right; word-break:break-word; }
.txn-drawer-divider { height:0.5px; background:#e0e0e0; margin:12px 0; }
.txn-drawer-footer { padding:12px 16px; border-top:0.5px solid #e0e0e0; display:flex; justify-content:flex-end; gap:8px; flex-shrink:0; }

/* ── Comment cards ── */
.aml-comment-card { border:0.5px solid #e0e0e0; border-radius:10px; overflow:hidden; }
.aml-comment-header { display:flex; align-items:center; padding:10px 14px; background:#fafafa; border-bottom:0.5px solid #e0e0e0; }
.aml-comment-avatar { width:28px; height:28px; border-radius:50%; background:#E6F1FB; color:#185FA5; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; font-family:monospace; flex-shrink:0; }
.aml-comment-body { padding:12px 14px; font-size:13px; white-space:pre-wrap; line-height:1.6; }

/* ── Stages ── */
.aml-stage-card { border:0.5px solid #e0e0e0; border-radius:12px; overflow:hidden; background:white; }
.aml-stage-card.aml-stage-blocked { background:#fafafa; }
.aml-stage-card.aml-stage-active  { border-color:#B5D4F4; background:#f0f7ff; }
.aml-stage-head { display:flex; align-items:center; padding:12px 16px; }
.aml-stage-num { width:24px; height:24px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; flex-shrink:0; }

/* ── Action rows ── */
.aml-action-row { display:flex; align-items:flex-start; padding:12px 16px; border-bottom:0.5px solid #f0f0f0; }
.aml-action-row:last-child { border-bottom:none; }
.aml-action-done  { background:#f8fdf5; }
.aml-action-waived { background:#fffbf5; }
.aml-action-btn { font-size:11px; padding:3px 10px; border-radius:6px; border:0.5px solid #ddd; background:transparent; cursor:pointer; font-family:inherit; transition:all 0.1s; }
.aml-action-btn:disabled { opacity:0.4; cursor:not-allowed; }
.aml-action-btn--primary { background:#185FA5; color:white; border-color:#185FA5; }
.aml-action-btn--primary:hover:not(:disabled) { background:#0C447C; }
.aml-action-btn--ghost { background:#f5f5f5; color:#555; }
.aml-action-btn--ghost:hover:not(:disabled) { background:#eee; }
.aml-action-btn--warn { background:#FAEEDA; color:#854F0B; border-color:#FAC775; }
.aml-action-btn--warn:hover:not(:disabled) { background:#FAC775; }
.min-width-0 { min-width:0; }
</style>